/**
 * Image compression presets for report PDF export.
 *
 * Values MUST match QUALITY_PRESETS in
 * cdcc-backend/playwright_service/src/services/imageCompression.js — the Node
 * service is the authority, this table exists so the export dialog can label
 * and estimate the options without a round trip.
 *
 * The UI intentionally offers a subset: PdfImageQuality lists everything the
 * API accepts, PDF_QUALITY_PRESETS lists what we actually show. 'compact' is
 * still supported server-side but deliberately not offered.
 */

export type PdfImageQuality = 'original' | 'high' | 'balanced' | 'compact';

export interface PdfQualityPreset {
  key: PdfImageQuality;
  label: string;
  /** Approximate effective DPI of report photos at this setting. */
  dpi: number;
  /** Max photo width in px handed to Chromium. null means "leave untouched". */
  maxWidth: number | null;
}

/** Photos are stored at this width, so it is the baseline every estimate scales from. */
export const SOURCE_PHOTO_WIDTH = 1400;

/** Client target: exports should come in at or under this. */
export const TARGET_SIZE_BYTES = 10 * 1024 * 1024;

export const PDF_QUALITY_PRESETS: PdfQualityPreset[] = [
  { key: 'original', label: 'Original', dpi: 420, maxWidth: null },
  { key: 'high', label: 'High', dpi: 200, maxWidth: 760 },
  { key: 'balanced', label: 'Balanced', dpi: 150, maxWidth: 570 },
];

export const DEFAULT_EXPORT_QUALITY: PdfImageQuality = 'high';

/** Size breakdown reported by the PDF service via X-Pdf-* response headers. */
export interface PdfSizeInfo {
  totalBytes: number;
  imageBytes: number;
  imageCount: number;
  imageQuality: string;
}

/**
 * Empirical correction applied on top of the (width ratio)^2 term.
 *
 * Chromium does not embed the source JPEG as-is — it rasterizes and stores the
 * result — so the re-encoded size does not follow pixel count alone. This
 * constant absorbs that difference and SHOULD be recalibrated against the
 * "PDF breakdown" figures the PDF service logs for a real photo-heavy report.
 */
const RE_ENCODE_FACTOR = 0.85;

/**
 * Estimates the exported size at a given preset from a measured `original` render.
 * Deliberately approximate — always present the result to the user as an estimate,
 * and show the real size once the PDF has actually been generated.
 */
export const estimatePresetSize = (baseline: PdfSizeInfo, preset: PdfQualityPreset): number => {
  if (preset.maxWidth === null) return baseline.totalBytes;

  const nonImageBytes = Math.max(0, baseline.totalBytes - baseline.imageBytes);
  const widthRatio = preset.maxWidth / SOURCE_PHOTO_WIDTH;
  const estimatedImageBytes = baseline.imageBytes * widthRatio * widthRatio * RE_ENCODE_FACTOR;

  return Math.round(nonImageBytes + estimatedImageBytes);
};

export const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '—';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};
